import { isNumber } from 'lodash'
import { QuestionProcessingStatuses } from '../constants/question-processing-statuses'

/**
 * Get the relevant answer for a question, optionally filtered by focusedQuestionRubricId.
 * With flattened answers, each answer corresponds to a single questionRubric.
 * 
 * @param {Array} answers - Flattened answers (one per questionRubric)
 * @param {Object} question - The Question entity
 * @param {number} focusedQuestionRubricId - Optional specific questionRubricId to find
 * @returns {Object|null} The answer object
 */
export const getRelevantAnswer = (answers, question, focusedQuestionRubricId) => {
    if (!answers?.length || !question) return null

    // If focusedQuestionRubricId provided, find that specific answer
    if (isNumber(focusedQuestionRubricId)) {
        return answers.find(a => a.questionRubricId === focusedQuestionRubricId)
    }

    // Otherwise, find first answer for this question (by questionId)
    return answers.find(a => a.questionId === question.id)
}

/**
 * Get an answer by QuestionRubric.
 * Each answer in the normalized data corresponds to a single questionRubric
 * and shares the same content from the submission.
 * 
 * @param {Array} answers - List of answers from submission (flattened, one per questionRubric)
 * @param {Object} questionRubric - The QuestionRubric (with id = questionRubricId)
 * @param {Object} question - The parent Question (for fallback lookup)
 * @returns {Object|null} The answer object
 */
export const getAnswerByRubric = (answers, questionRubric, question) => {
    if (!answers?.length) return null

    // Find answer by questionRubricId
    if (questionRubric?.id) {
        const answer = answers.find(a => a.questionRubricId === questionRubric.id)
        if (answer) return answer
    }

    // Fallback: find by questionId (for questions with single/no rubric)
    if (question?.id) {
        return answers.find(a => a.questionId === question.id)
    }

    return null
}

/**
 * Get training stats for all questionRubrics across all questions.
 * @param {Array} questions - Questions with enriched questionRubrics
 */
export const getQuestionRubricsTrainingStats = questions => {
    const allQuestionRubrics = questions?.flatMap(q => q.questionRubrics || []) || []
    const numberOfTrainedRubrics = allQuestionRubrics.filter(qr => qr.lastTrained)?.length || 0
    const percentOfTrainedRubrics = Math.round(
        allQuestionRubrics.length > 0 ? (numberOfTrainedRubrics / allQuestionRubrics.length) * 100 : 0,
    )
    const trainableRubricsNumber = allQuestionRubrics.length
    const allRubricsTrained = numberOfTrainedRubrics === trainableRubricsNumber

    return { 
        numberOfTrainedRubrics, 
        percentOfTrainedRubrics, 
        trainableRubricsNumber, 
        allRubricsTrained,
    }
}

/**
 * Get processing info for a QuestionRubric.
 * @param {Object} questionRubric - The QuestionRubric entity
 */
export const getQuestionRubricProcessingInfo = questionRubric => {
    if (!questionRubric) {
        return {
            message: 'Invalid rubric',
            progress: 0,
        }
    }

    let message = questionRubric.processing?.message || ''
    let progress = questionRubric.processing?.progress || 0

    if (questionRubric.processing?.status === QuestionProcessingStatuses.READY) {
        message = questionRubric.lastTrained ? 'Trained' : 'Not Trained'
        progress = questionRubric.lastTrained ? 100 : 0
    }

    return {
        message,
        progress,
    }
}

/**
 * Get aggregated processing info for a Question (all its questionRubrics).
 * @param {Object} question - The Question with enriched questionRubrics
 */
export const getQuestionProcessingInfo = question => {
    if (!question) {
        return {
            message: 'Invalid question',
            progress: 0,
        }
    }

    const questionRubrics = question.questionRubrics || []
    if (questionRubrics.length === 0) {
        return {
            message: 'No rubrics',
            progress: 0,
        }
    }

    const totalRubrics = questionRubrics.length
    const trainedRubrics = questionRubrics.filter(qr => qr.lastTrained).length

    const progress = Math.round((trainedRubrics / totalRubrics) * 100)
    const message = `${trainedRubrics}/${totalRubrics} Trained`

    return {
        message,
        progress,
    }
}
